import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert, AlertTitle, Button } from '@mui/material';
import { Lock, Home } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const ProtectedRoute = ({ 
  children, 
  requiredRoles = [], 
  fallbackPath = '/login',
  showAccessDenied = true 
}) => {
  const { 
    loading, 
    isAuthenticated, 
    user, 
    hasRole,
    error 
  } = useAuth();
  const location = useLocation();

  // Loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={48} thickness={4} />
        <Box textAlign="center">
          <Box component="span" sx={{ 
            fontSize: '0.875rem', 
            color: 'text.secondary',
            fontWeight: 500
          }}>
            Verificando autenticação...
          </Box>
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Alert 
          severity="error" 
          sx={{ maxWidth: 500 }}
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </Button>
          }
        >
          <AlertTitle>Erro de Autenticação</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ 
          from: location,
          message: 'Você precisa fazer login para acessar esta página.'
        }} 
        replace 
      />
    );
  }

  // Check role permissions
  if (requiredRoles.length > 0 && !hasRole(requiredRoles)) {
    if (!showAccessDenied) {
      return <Navigate to="/dashboard" replace />;
    }

    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Alert 
          severity="warning" 
          sx={{ 
            maxWidth: 600,
            '& .MuiAlert-icon': {
              fontSize: '2rem'
            }
          }}
          icon={<Lock />}
          action={
            <Button 
              color="inherit" 
              size="small" 
              startIcon={<Home />}
              onClick={() => window.location.href = '/dashboard'}
            >
              Ir para Dashboard
            </Button>
          }
        >
          <AlertTitle>Acesso Negado</AlertTitle>
          <Box sx={{ mb: 1 }}>
            Você não tem permissão para acessar esta página.
          </Box>
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            <strong>Seu nível:</strong> {user?.role || 'Desconhecido'}<br />
            <strong>Necessário:</strong> {requiredRoles.join(', ')}
          </Box>
        </Alert>
      </Box>
    );
  }

  // All checks passed - render children
  return children;
};

// Higher-order component para proteção de rotas
export const withProtection = (Component, requiredRoles = []) => {
  return function ProtectedComponent(props) {
    return (
      <ProtectedRoute requiredRoles={requiredRoles}>
        <Component {...props} />
      </ProtectedRoute>
    );
  };
};

// Componente específico para páginas administrativas
export const AdminRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={['admin', 'owner']}>
    {children}
  </ProtectedRoute>
);

// Componente específico para managers
export const ManagerRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={['manager', 'admin', 'owner']}>
    {children}
  </ProtectedRoute>
);

// Componente específico para analistas
export const AnalystRoute = ({ children }) => (
  <ProtectedRoute requiredRoles={['analyst', 'manager', 'admin', 'owner']}>
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;






